import { useEffect, useMemo, useState } from 'react';
import { Topbar } from '@/components/Topbar';
import { Button } from '@/components/Button';
import { EmptyState } from '@/components/EmptyState';
import { Modal } from '@/components/Modal';
import { Input } from '@/components/Input';
import { Select } from '@/components/Select';
import { FormField } from '@/components/FormField';
import { StatusPill } from '@/components/StatusPill';
import { SearchInput } from '@/components/SearchInput';
import {
  TabBody, TabToolbar, TableCard, Th, Td, Tr, SkeletonTable, InlineHint,
  AudienceTypePill, audienceStatusVariant, isLookalikeType, titleCase, fmtCount,
} from '@/components/tiktok/measurementShared';
import { tiktokService, TiktokAudience } from '@/services/tiktok.service';
import { contactGroupsService, ContactGroup } from '@/services/contact-groups.service';
import { Users, Plus, Upload, Sparkles, RefreshCw } from 'lucide-react';
import toast from 'react-hot-toast';

const COMMON_COUNTRIES: Array<{ code: string; label: string }> = [
  { code: 'AE', label: 'United Arab Emirates' },
  { code: 'SA', label: 'Saudi Arabia' },
  { code: 'EG', label: 'Egypt' },
  { code: 'PK', label: 'Pakistan' },
  { code: 'IN', label: 'India' },
  { code: 'US', label: 'United States' },
  { code: 'GB', label: 'United Kingdom' },
  { code: 'DE', label: 'Germany' },
  { code: 'FR', label: 'France' },
  { code: 'BR', label: 'Brazil' },
  { code: 'MX', label: 'Mexico' },
  { code: 'ID', label: 'Indonesia' },
];

type Props = {
  /** Rendered inside a TabbedPage shell — suppress the page Topbar. */
  embedded?: boolean;
};

export default function TiktokAudiences({ embedded = false }: Props = {}) {
  const [audiences, setAudiences] = useState<TiktokAudience[] | null>(null);
  const [groups, setGroups] = useState<ContactGroup[]>([]);
  const [refreshing, setRefreshing] = useState(false);
  const [query, setQuery] = useState('');

  // Create-from-group modal state
  const [createOpen, setCreateOpen] = useState(false);
  const [groupUid, setGroupUid] = useState('');
  const [audienceName, setAudienceName] = useState('');
  const [busy, setBusy] = useState(false);

  // Lookalike modal state
  const [lookOpen, setLookOpen] = useState(false);
  const [seedId, setSeedId] = useState('');
  const [lookName, setLookName] = useState('');
  const [country, setCountry] = useState('AE');
  const [sizeFactor, setSizeFactor] = useState(0.05);
  const [lookBusy, setLookBusy] = useState(false);

  const refresh = async (announce = false) => {
    setRefreshing(true);
    try {
      const r = await tiktokService.listAudiences();
      setAudiences(r);
      if (announce) toast.success(`${r.length} audience${r.length === 1 ? '' : 's'} loaded`);
    } catch {
      setAudiences([]);
    } finally { setRefreshing(false); }
  };

  useEffect(() => {
    refresh();
    contactGroupsService.list().then(setGroups).catch(() => setGroups([]));
  }, []);

  const openLookalike = (seed?: TiktokAudience) => {
    if (seed) { setSeedId(seed.audienceId); setLookName(`${seed.name} — Lookalike`); }
    setLookOpen(true);
  };

  const createLookalike = async () => {
    if (!seedId) { toast.error('Pick a seed audience'); return; }
    if (!lookName.trim()) { toast.error('Name is required'); return; }
    if (!country) { toast.error('Pick a country'); return; }
    setLookBusy(true);
    try {
      const r = await tiktokService.createLookalike({
        seedAudienceId: seedId,
        name: lookName.trim(),
        countryCode: country,
        sizeFactor,
      });
      toast.success(`Lookalike audience created · ID ${r.audienceId}. Building takes 1–24h on TikTok's side.`);
      setLookOpen(false);
      setSeedId(''); setLookName(''); setCountry('AE'); setSizeFactor(0.05);
      await refresh();
    } catch {
      // Axios interceptor already toasted the backend message; avoid duplicates.
    } finally { setLookBusy(false); }
  };

  const create = async () => {
    if (!groupUid || !audienceName.trim()) { toast.error('Pick a group and name'); return; }
    setBusy(true);
    try {
      const r = await tiktokService.createAudienceFromGroup({ groupUid, audienceName: audienceName.trim() });
      toast.success(`Audience "${audienceName}" created — uploaded ${r.uploaded} hashed identifier(s). Audience ID: ${r.audienceId}`);
      setCreateOpen(false);
      setAudienceName(''); setGroupUid('');
      await refresh();
    } catch {
      // Axios interceptor already toasted the backend message; avoid duplicates.
    } finally { setBusy(false); }
  };

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    const list = audiences ?? [];
    return q ? list.filter((a) => a.name.toLowerCase().includes(q) || a.audienceId.includes(q)) : list;
  }, [audiences, query]);

  const counts = useMemo(() => {
    const list = audiences ?? [];
    const look = list.filter((a) => isLookalikeType(a.audienceType)).length;
    return { custom: list.length - look, lookalike: look };
  }, [audiences]);

  const loading = audiences === null;
  const hasData = !!audiences && audiences.length > 0;

  const actions = (
    <>
      <Button variant="secondary" onClick={() => refresh(true)} loading={refreshing}><RefreshCw size={14} /> Refresh</Button>
      <Button variant="secondary" onClick={() => openLookalike()} disabled={!hasData} title={hasData ? undefined : 'Needs a custom audience to seed from'}>
        <Sparkles size={14} /> New lookalike
      </Button>
      <Button variant="primary" onClick={() => setCreateOpen(true)}><Plus size={14} /> Create audience</Button>
    </>
  );

  return (
    <>
      {!embedded && (
        <Topbar
          title="Audiences"
          breadcrumb="TikTok Ads / Audiences"
          description="Custom audiences for ad targeting, built from CRM contact groups. Phone + email are SHA-256 hashed before upload."
        />
      )}

      <TabBody>
        <TabToolbar
          title="Audiences"
          count={audiences?.length ?? null}
          subtitle={hasData
            ? `${counts.custom} custom · ${counts.lookalike} lookalike · phone and email are SHA-256 hashed locally before they reach TikTok.`
            : 'Custom audiences built from CRM contact groups; phone and email are SHA-256 hashed locally before they reach TikTok.'}
          right={actions}
        />

        {loading && <SkeletonTable rows={5} cols={5} />}

        {!loading && !hasData && (
          <EmptyState
            icon={<Users size={32} />}
            title="No audiences yet"
            description="Creating an audience is a two-step DMP flow: the CRM first uploads the hashed identifiers from a contact group, then creates the audience from that upload — pick a group to start."
            action={<Button onClick={() => setCreateOpen(true)}><Plus size={14} /> Create audience</Button>}
          />
        )}

        {hasData && (
          <>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <SearchInput value={query} onChange={setQuery} placeholder="Filter by name or ID…" />
              <InlineHint>New audiences show as calculating until TikTok finishes matching, usually within a few hours.</InlineHint>
            </div>

            <TableCard minWidth={760}>
              <thead>
                <tr>
                  <Th>Audience</Th>
                  <Th>Type</Th>
                  <Th align="right">Size</Th>
                  <Th>Status</Th>
                  <Th>Source</Th>
                  <Th align="right" width="140px"><span className="sr-only">Actions</span></Th>
                </tr>
              </thead>
              <tbody>
                {visible.length === 0 && (
                  <tr><td colSpan={6} className="px-5 py-12 text-center text-sm text-text-muted">No audiences match “{query}”.</td></tr>
                )}
                {visible.map((a) => (
                  <Tr key={a.audienceId}>
                    <Td>
                      <div className="flex items-center gap-3">
                        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary-soft text-primary">
                          {isLookalikeType(a.audienceType) ? <Sparkles size={15} /> : <Users size={15} />}
                        </span>
                        <div className="min-w-0">
                          <div className="truncate font-medium text-text-primary">{a.name}</div>
                          <div className="mt-0.5 font-mono text-2xs text-text-muted">{a.audienceId}</div>
                        </div>
                      </div>
                    </Td>
                    <Td><AudienceTypePill type={a.audienceType} /></Td>
                    <Td align="right" muted={a.size == null}>{a.size != null ? fmtCount(a.size) : '—'}</Td>
                    <Td>
                      {a.status
                        ? <StatusPill status={a.status} label={titleCase(a.status)} variant={audienceStatusVariant(a.status)} />
                        : <span className="text-text-muted">—</span>}
                    </Td>
                    <Td muted>
                      {isLookalikeType(a.audienceType) ? 'Lookalike of a custom audience' : titleCase(a.audienceType) === '—' ? 'Uploaded identifiers' : titleCase(a.audienceType)}
                    </Td>
                    <Td align="right">
                      <Button size="sm" variant="secondary" onClick={() => openLookalike(a)} title="Build a lookalike from this audience">
                        <Sparkles size={12} /> Lookalike
                      </Button>
                    </Td>
                  </Tr>
                ))}
              </tbody>
            </TableCard>
          </>
        )}
      </TabBody>

      <Modal open={lookOpen} onClose={() => !lookBusy && setLookOpen(false)} title="Build a lookalike audience" widthClass="max-w-lg">
        <div className="space-y-1">
          <p className="mb-4 text-xs leading-relaxed text-text-muted">
            Lookalikes find new users who resemble an existing custom audience. Pick the seed and target country, then choose how broad to go: 1% is the closest match, 20% is the broadest reach. TikTok takes 1–24 hours to build the audience after creation.
          </p>
          <FormField label="Seed audience" required>
            <Select value={seedId} onChange={(e) => setSeedId(e.target.value)}>
              <option value="">— Pick a custom audience —</option>
              {(audiences ?? []).map((a) => (
                <option key={a.audienceId} value={a.audienceId}>
                  {a.name} ({a.audienceId})
                </option>
              ))}
            </Select>
          </FormField>
          <FormField label="Lookalike name" required>
            <Input value={lookName} onChange={(e) => setLookName(e.target.value)} placeholder="UAE customers — Lookalike 5%" />
          </FormField>
          <div className="grid grid-cols-2 gap-3">
            <FormField label="Country" required>
              <Select value={country} onChange={(e) => setCountry(e.target.value)}>
                {COMMON_COUNTRIES.map((c) => <option key={c.code} value={c.code}>{c.label} ({c.code})</option>)}
              </Select>
            </FormField>
            <FormField label={`Match breadth · ${(sizeFactor * 100).toFixed(0)}%`} hintBelow hint="1% = closest match · 20% = broadest reach">
              <input
                type="range"
                min={0.01}
                max={0.20}
                step={0.01}
                value={sizeFactor}
                onChange={(e) => setSizeFactor(parseFloat(e.target.value))}
                className="mt-2 w-full accent-primary"
              />
            </FormField>
          </div>
        </div>
        <div className="-mx-4 -mb-4 flex justify-end gap-2 border-t border-border px-4 py-4 sm:-mx-6 sm:-mb-5 sm:px-6">
          <Button variant="secondary" onClick={() => setLookOpen(false)} disabled={lookBusy}>Cancel</Button>
          <Button variant="primary" onClick={createLookalike} loading={lookBusy} disabled={lookBusy || !seedId || !lookName.trim()}>
            <Sparkles size={14} /> Build lookalike
          </Button>
        </div>
      </Modal>

      <Modal open={createOpen} onClose={() => !busy && setCreateOpen(false)} title="Create custom audience" widthClass="max-w-lg">
        <div className="space-y-1">
          <ol className="mb-4 grid gap-2 sm:grid-cols-2">
            {[
              ['1', 'Upload', 'Phone + email from every contact in the group are SHA-256 hashed locally and uploaded to TikTok.'],
              ['2', 'Create', 'TikTok builds the audience from that upload; matching finishes within a few hours.'],
            ].map(([n, t, d]) => (
              <li key={n} className="flex gap-3 rounded-xl border border-border bg-surface-alt/40 p-3">
                <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary-soft text-xs font-semibold text-primary">{n}</span>
                <div>
                  <div className="text-sm font-medium text-text-primary">{t}</div>
                  <div className="mt-0.5 text-2xs leading-relaxed text-text-muted">{d}</div>
                </div>
              </li>
            ))}
          </ol>
          <FormField label="Audience name" required>
            <Input value={audienceName} onChange={(e) => setAudienceName(e.target.value)} placeholder="UAE customers — past 30 days" autoFocus />
          </FormField>
          <FormField label="Contact group" required hintBelow hint="PII never leaves the CRM in plaintext.">
            <Select value={groupUid} onChange={(e) => setGroupUid(e.target.value)}>
              <option value="">— Select a group —</option>
              {groups.map((g) => (
                <option key={g.groupUid} value={g.groupUid}>
                  {g.name} ({g.contactCount ?? 0} contacts)
                </option>
              ))}
            </Select>
          </FormField>
        </div>
        <div className="-mx-4 -mb-4 flex justify-end gap-2 border-t border-border px-4 py-4 sm:-mx-6 sm:-mb-5 sm:px-6">
          <Button variant="secondary" onClick={() => setCreateOpen(false)} disabled={busy}>Cancel</Button>
          <Button variant="primary" onClick={create} loading={busy} disabled={busy || !groupUid || !audienceName.trim()}>
            <Upload size={14} /> Upload + create
          </Button>
        </div>
      </Modal>
    </>
  );
}
